import type {
  CartRequest,
  CreateCheckoutRequest,
  Product,
  Subscription,
} from "./model"
import { baseApi, extractChannelEvent, subscribe } from "@/base"
import { invariant } from "@/utils"

const api = baseApi
  .enhanceEndpoints({ addTagTypes: ["Subscription"] })
  .injectEndpoints({
    endpoints: (build) => ({
      getProducts: build.query<Product[], void>({
        query: () => "products",
      }),
      createCheckoutSession: build.query<
        { clientSecret: string },
        CreateCheckoutRequest
      >({
        query: (body) => ({
          url: `checkout`,
          method: "POST",
          body,
        }),
      }),
      getCheckoutSession: build.query<{ success: boolean }, string>({
        query: (id) => ({
          url: `checkout/session/${id}`,
          method: "POST",
        }),
      }),
      getSubscription: build.query<Subscription, void>({
        query: () => `subscription`,
        providesTags: ["Subscription"],
        onCacheEntryAdded: async (
          arg,
          { cacheDataLoaded, cacheEntryRemoved, dispatch },
        ) => {
          const { meta } = await cacheDataLoaded
          invariant(meta?.response)
          const [channelName, eventName] = extractChannelEvent(meta.response)

          const unbind = await subscribe(
            channelName,
            () => dispatch(baseApi.endpoints.ablyAuth.initiate()).unwrap(),
            {
              [eventName]: () => {
                // Just refetch as there is no way to know if this subscription is the current one
                void dispatch(
                  api.endpoints.getSubscription.initiate(arg, {
                    subscribe: false,
                    forceRefetch: true,
                  }),
                )
              },
            },
          )

          await cacheEntryRemoved
          unbind()
        },
      }),
      changeSubscription: build.mutation<Subscription, CartRequest>({
        query: (body) => ({
          url: `subscription/change`,
          method: "POST",
          body,
        }),
        async onQueryStarted(_args, { dispatch, queryFulfilled }) {
          try {
            const { data: subscription } = await queryFulfilled

            await dispatch(
              api.util.upsertQueryData(
                "getSubscription",
                undefined,
                subscription,
              ),
            )
          } catch {
            // noop
          }
        },
      }),
      createPortalSession: build.mutation<{ url: string }, void>({
        query: () => ({
          url: `customerportal`,
          method: "POST",
        }),
      }),
    }),
  })

export const {
  useGetProductsQuery,
  useCreateCheckoutSessionQuery,
  useGetCheckoutSessionQuery,
  useGetSubscriptionQuery,
  useChangeSubscriptionMutation,
  useCreatePortalSessionMutation,
} = api
